import { useTu } from "@src/appV2/i18n/useTu";
import { Box, ButtonBase } from "@mui/material";
import { useCallback } from "react";
import { Text } from "../Text";
export type AmPm = "am" | "pm";

interface AmPmToggleProps {
  value: AmPm;
  onChange: (value: AmPm) => void;
  disabled?: boolean;
}

/**
 * A pill-style AM/PM toggle component.
 * Shows AM and PM options with the selected one having a border pill around it.
 * Matches the Berlin design system time picker component.
 */
export function AmPmToggle(props: AmPmToggleProps) {
  const { tu } = useTu("common");

  const { value, onChange, disabled = false } = props;

  const handleAmClick = useCallback(() => {
    if (!disabled) {
      onChange("am");
    }
  }, [disabled, onChange]);

  const handlePmClick = useCallback(() => {
    if (!disabled) {
      onChange("pm");
    }
  }, [disabled, onChange]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.borderRadius?.small,
      })}
    >
      <ButtonBase
        aria-label="AM"
        aria-pressed={value === "am"}
        disabled={disabled}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingX: "5px",
          paddingY: "2px",
          borderRadius: theme.borderRadius?.small,
          // Only selected option has a border
          border: value === "am" ? `1px solid ${theme.palette.text.primary}` : "none",
          backgroundColor: "transparent",
          cursor: disabled ? "default" : "pointer",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        })}
        onClick={handleAmClick}
      >
        <Text
          inline
          semibold
          sx={(theme) => ({
            color: value === "am" ? theme.palette.text.primary : theme.palette.text.disabled,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1,
          })}
          variant="body2"
        >
          {tu("am")}
        </Text>
      </ButtonBase>
      <ButtonBase
        aria-label="PM"
        aria-pressed={value === "pm"}
        disabled={disabled}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingX: "5px",
          paddingY: "2px",
          borderRadius: theme.borderRadius?.small,
          // Only selected option has a border
          border: value === "pm" ? `1px solid ${theme.palette.text.primary}` : "none",
          backgroundColor: "transparent",
          cursor: disabled ? "default" : "pointer",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        })}
        onClick={handlePmClick}
      >
        <Text
          inline
          semibold
          sx={(theme) => ({
            color: value === "pm" ? theme.palette.text.primary : theme.palette.text.disabled,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1,
          })}
          variant="body2"
        >
          {tu("pm")}
        </Text>
      </ButtonBase>
    </Box>
  );
}
